import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2 } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import {
  ClinicProgram,
  DisplayMode,
  clinicProgramService,
} from '@/services/clinicProgramService'

interface ClinicProgramFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  clinicId: string
  mode: 'create' | 'edit'
  program?: ClinicProgram | null
  onSuccess: () => void
  clinicArchetype?: 'guided' | 'direct' | 'hybrid' | null
}

interface ProgramFormState {
  title: string
  subtitle: string
  inclusions: string
  target: string
  display_mode: DisplayMode
}

const MAX_LENGTH = 255

const defaultFormState: ProgramFormState = {
  title: '',
  subtitle: '',
  inclusions: '',
  target: '',
  display_mode: 'medication_select',
}

const ClinicProgramFormDialog = ({
  open,
  onOpenChange,
  clinicId,
  mode,
  program,
  onSuccess,
  clinicArchetype,
}: ClinicProgramFormDialogProps) => {
  const { toast } = useToast()
  const [isSaving, setIsSaving] = useState(false)
  const [form, setForm] = useState<ProgramFormState>(defaultFormState)

  const showDisplayMode = mode === 'create' && clinicArchetype === 'hybrid'

  useEffect(() => {
    if (!open) return

    if (mode === 'edit' && program) {
      setForm({
        title: program.title || '',
        subtitle: program.subtitle || '',
        inclusions: program.inclusions || '',
        target: program.target || '',
        display_mode: program.display_mode ?? 'medication_select',
      })
      return
    }

    setForm({
      ...defaultFormState,
      display_mode: clinicArchetype === 'guided' ? 'protocol_showcase' : 'medication_select',
    })
  }, [open, mode, program, clinicArchetype])

  const handleChange = (field: keyof ProgramFormState, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  const textFields: (keyof Pick<ProgramFormState, 'title' | 'subtitle' | 'inclusions' | 'target'>)[] = [
    'title',
    'subtitle',
    'inclusions',
    'target',
  ]
  const tooLong = textFields.some(k => form[k].length > MAX_LENGTH)
  const isInvalid =
    !form.title.trim() ||
    !form.subtitle.trim() ||
    !form.inclusions.trim() ||
    !form.target.trim() ||
    tooLong

  const handleSubmit = async () => {
    if (isInvalid || !clinicId) return

    try {
      setIsSaving(true)

      if (mode === 'edit' && program) {
        await clinicProgramService.updateProgram(clinicId, program.id, {
          title: form.title.trim(),
          subtitle: form.subtitle.trim(),
          inclusions: form.inclusions.trim(),
          target: form.target.trim(),
        })
      } else {
        const displayModePayload: { display_mode?: DisplayMode } =
          clinicArchetype === 'guided'
            ? { display_mode: 'protocol_showcase' }
            : clinicArchetype === 'hybrid'
              ? { display_mode: form.display_mode }
              : {}
        await clinicProgramService.createProgram(clinicId, {
          title: form.title.trim(),
          subtitle: form.subtitle.trim(),
          inclusions: form.inclusions.trim(),
          target: form.target.trim(),
          ...displayModePayload,
        })
      }

      toast({
        title: 'Success',
        description: mode === 'edit' ? 'Program updated successfully' : 'Program created successfully',
        variant: 'success',
      })
      onSuccess()
      onOpenChange(false)
    } catch (error) {
      console.error(error)
      toast({
        title: 'Error',
        description: (error as { message?: string })?.message
          || (mode === 'edit' ? 'Failed to update program' : 'Failed to create program'),
        variant: 'destructive',
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{mode === 'edit' ? 'Edit Program' : 'Add Program'}</DialogTitle>
          <DialogDescription>
            {mode === 'edit'
              ? 'Update this clinic program details.'
              : 'Create a new clinic program for prescriptions.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {([
            { key: 'title' as const, label: 'Title', placeholder: 'Program title' },
            { key: 'subtitle' as const, label: 'Subtitle', placeholder: 'Program subtitle' },
            { key: 'inclusions' as const, label: 'Inclusions', placeholder: 'What is included' },
            { key: 'target' as const, label: 'Target', placeholder: 'Target audience/outcome' },
          ]).map(({ key, label, placeholder }) => {
            const overLimit = form[key].length > MAX_LENGTH
            return (
              <div className="space-y-1.5" key={key}>
                <div className="flex items-center justify-between">
                  <Label htmlFor={`program-${key}`}>
                    {label} <span className="text-destructive">*</span>
                  </Label>
                  <span className={`text-xs ${overLimit ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>
                    {form[key].length}/{MAX_LENGTH}
                  </span>
                </div>
                <Input
                  id={`program-${key}`}
                  value={form[key]}
                  onChange={e => handleChange(key, e.target.value)}
                  placeholder={placeholder}
                  className={overLimit ? 'border-destructive focus-visible:ring-destructive' : ''}
                />
                {overLimit && (
                  <p className="text-xs text-destructive">
                    {label} must be {MAX_LENGTH} characters or fewer
                  </p>
                )}
              </div>
            )
          })}

          {showDisplayMode && (
            <div className="space-y-2">
              <Label>Display mode</Label>
              <div className="flex items-center gap-4 pt-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="display_mode"
                    checked={form.display_mode === 'medication_select'}
                    onChange={() => handleChange('display_mode', 'medication_select')}
                    className="rounded-full border-slate-300 text-brand-teal focus:ring-brand-teal"
                  />
                  <span className="text-sm">In-house</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="display_mode"
                    checked={form.display_mode === 'protocol_showcase'}
                    onChange={() => handleChange('display_mode', 'protocol_showcase')}
                    className="rounded-full border-slate-300 text-brand-teal focus:ring-brand-teal"
                  />
                  <span className="text-sm">Nimbus-Routed</span>
                </label>
              </div>
              <p className="text-xs text-muted-foreground">
                Choose how this program is offered: In-house (clinic prescribers) or Nimbus-Routed (Nimbus delivers).
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving || isInvalid}>
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {mode === 'edit' ? 'Save Changes' : 'Create Program'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ClinicProgramFormDialog
